import { defineStore } from 'pinia'
import httpClient from '@/api/httpClient'
import { useMessagesStore } from '@/stores/messages'

const defaultExportFormat = () => ({
  id: null,
  name: null,
  source_type: null,
  parser: null,
  data_formatter: null,
  content_type: null,
  extension: null,
  source_template: null,
  transform_id: null,
  attachment: false,
  auto_export: false,
  master_template: false,
  batch_export: false,
  upload_filename: null
})

export const useExportFormatStore = defineStore('exportFormat', {
  state: () => ({
    exportFormat: defaultExportFormat(),
    formData: {
      source_types: [],
      parsers: [],
      data_formatters: [],
      placeholders: {},
      transforms: [],
      custom_field_names: []
    }
  }),

  actions: {
    async fetch(id) {
      return httpClient.get(`/api/internal/export_formats/${id}`)
        .then(response => {
          this.exportFormat = response.data.export_format
        })
        .catch(err => {
          useMessagesStore().addError(err)
        })
    },

    async fetchFormData() {
      return httpClient.get('/api/internal/export_formats/form_data')
        .then(response => {
          this.formData = response.data.form_data
        })
        .catch(err => {
          useMessagesStore().addError(err)
        })
    },

    async save(file = null) {
      return this.exportFormat.id ? this.update(file) : this.create(file)
    },

    async create(file = null) {
      const data = this._buildPayload(file)

      return httpClient.post('/api/internal/export_formats', data)
        .then(resp => {
          const { data: { export_format } } = resp
          this.exportFormat = export_format
          useMessagesStore().addNotice(`${export_format.name} created`)
          return resp
        })
        .catch(err => {
          useMessagesStore().addError(err)
          throw err
        })
    },

    async update(file = null) {
      const data = this._buildPayload(file)

      return httpClient.patch(`/api/internal/export_formats/${this.exportFormat.id}`, data)
        .then(resp => {
          const { data: { export_format } } = resp
          this.exportFormat = export_format
          useMessagesStore().addNotice(`${export_format.name} saved`)
          return resp
        })
        .catch(err => {
          useMessagesStore().addError(err)
          throw err
        })
    },

    async destroy(id) {
      return httpClient.delete(`/api/internal/export_formats/${id}`)
        .then(() => {
          useMessagesStore().addNotice('Export format deleted')
        })
        .catch(err => {
          useMessagesStore().addError(err)
          throw err
        })
    },

    _buildPayload(file) {
      if (file) {
        const formData = new FormData()
        const ef = this.exportFormat
        const fields = {
          name: ef.name,
          source_type: ef.source_type,
          parser: ef.parser,
          data_formatter: ef.data_formatter,
          content_type: ef.content_type,
          extension: ef.extension,
          source_template: ef.source_template || '',
          transform_id: ef.transform_id || '',
          attachment: ef.attachment,
          auto_export: ef.auto_export,
          master_template: ef.master_template,
          batch_export: ef.batch_export
        }
        Object.entries(fields).forEach(([key, val]) => {
          formData.append(`export_format[${key}]`, val ?? '')
        })
        formData.append('export_format[upload]', file)
        return formData
      }

      return { export_format: this.exportFormat }
    }
  }
})
